<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { PasswordRequirement } from "@voltalis/ui-kit/components";
import { useForm } from "vee-validate";
import { z } from "zod";

import { useTypedIntl } from "@/locales/typed-intl";
import {
  MIN_PASSWORD_LENGTH,
  containsLowercase,
  containsNumber,
  containsSpecialChar,
  containsUppercase,
  isPasswordValid,
} from "@/utils/string-utils";

import Password from "primevue/password";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IEmits {
  (e: "submit", password: string): void;
}

const emits = defineEmits<IEmits>();
const { t, k, rawT } = useTypedIntl();

const { handleSubmit, errors, defineField } = useForm({
  validationSchema: toTypedSchema(
    z
      .object({
        password: z.string(),
        passwordConfirmation: z.string(),
      })
      .superRefine(({ password, passwordConfirmation }, checkPassComplexity) => {
        if (!isPasswordValid(password)) {
          checkPassComplexity.addIssue({
            path: ["password"],
            code: "custom",
            message: "password_complexity_requirement",
          });
        }

        if (passwordConfirmation !== password) {
          checkPassComplexity.addIssue({
            path: ["passwordConfirmation"],
            code: "custom",
            message: "password_mismatch",
          });
        }
      }),
  ),
  initialValues: {
    password: "",
    passwordConfirmation: "",
  },
});

const [password] = defineField("password");
const [passwordConfirmation] = defineField("passwordConfirmation");

const submit = handleSubmit(async (values) => {
  emits("submit", values.password);
});
</script>

<template>
  <form
    v-focustrap
    class="flex flex-col gap-4"
    @submit="submit"
  >
    <div class="flex flex-col gap-2">
      <label class="font-medium">
        {{ t(k.components.passwordCreation.newPassword) }}
      </label>
      <Password
        v-model="password"
        data-test-id="password-input"
        :feedback="false"
        :invalid="!!errors.password"
        toggle-mask
      />
      <small
        v-if="errors.password"
        class="text-red-500"
      >
        {{ rawT(`veeValidate.messages.${errors.password}`) }}
      </small>
    </div>

    <div class="flex flex-col gap-2">
      <label class="font-medium">
        {{ t(k.components.passwordCreation.passwordConfirmation) }}
      </label>
      <Password
        v-model="passwordConfirmation"
        data-test-id="password-confirmation-input"
        :feedback="false"
        :invalid="!!errors.passwordConfirmation"
        toggle-mask
      />
      <small
        v-if="errors.passwordConfirmation"
        class="text-red-500"
      >
        {{ rawT(`veeValidate.messages.${errors.passwordConfirmation}`) }}
      </small>
    </div>

    <div class="border rounded-md border-slate-300 p-4 flex flex-col gap-2">
      <span class="font-semibold text-text-secondary">
        {{ t(k.components.passwordCreation.passwordShouldContain) }}
      </span>
      <div class="flex flex-col gap-1">
        <PasswordRequirement :is-valid="password!.length >= MIN_PASSWORD_LENGTH">
          {{ t(k.components.passwordCreation.atLeast10Char) }}
        </PasswordRequirement>
        <PasswordRequirement :is-valid="containsUppercase(password!)">
          {{ t(k.components.passwordCreation.oneUppercase) }}
        </PasswordRequirement>
        <PasswordRequirement :is-valid="containsLowercase(password!)">
          {{ t(k.components.passwordCreation.oneLowercase) }}
        </PasswordRequirement>
        <PasswordRequirement :is-valid="containsNumber(password!)">
          {{ t(k.components.passwordCreation.oneNumber) }}
        </PasswordRequirement>
        <PasswordRequirement :is-valid="containsSpecialChar(password!)">
          {{ t(k.components.passwordCreation.oneSpecialCharacter) }}
        </PasswordRequirement>
      </div>
    </div>

    <slot name="submitBtn">
      <HospitalityButton
        class="mt-2"
        :label="t(k.global.misc.submit)"
        type="submit"
      />
    </slot>
  </form>
</template>
